import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Check, X, ListChecks, RefreshCw, Trash2, Briefcase, Minus, Plus, Sparkles, CalendarDays } from "lucide-react";
import api from "../../lib/api";
import { useFocusRecord } from "../../lib/useFocusRecord";
import { DEPARTURES_2027, formatDepartureRange, GUARANTEE_MIN } from "../../data/holidays";

const STATUS_LABELS = { new: "Nouă", confirmed: "Confirmată", rejected: "Respinsă", completed: "Finalizată" };
const STATUS_STYLES = {
  new: "bg-terracotta text-white",
  confirmed: "bg-forest text-bone",
  rejected: "bg-destructive text-destructive-foreground",
  completed: "bg-moss text-white",
};

export default function HolidayEnquiriesPanel({ focusId = null }) {
  const [rows, setRows] = useState([]);
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/holiday-enquiries", { params: filter === "all" ? {} : { status_filter: filter } });
      setRows(r.data);
    } catch {
      toast.error("Nu s-au putut încărca cererile E-MTB Holidays.");
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => { load(); }, [load]);
  useFocusRecord(focusId, rows, loading, setSelected, "holiday-row-");

  const [seats, setSeats] = useState(null);
  const [showDeps, setShowDeps] = useState(false);
  useEffect(() => { api.get("/settings/departures").then((r) => setSeats(r.data.seats || {})).catch(() => {}); }, []);
  const setSeatsLeft = async (id, value) => {
    if (value < 0 || value > 10) return;
    try {
      const r = await api.put(`/settings/departures/${id}`, { seats_left: value });
      setSeats(r.data.seats || {});
      toast.success(value === 0 ? "Plecare marcată COMPLET." : `${value} locuri rămase`);
    } catch { toast.error("Eroare la actualizarea locurilor."); }
  };

  const [eb, setEb] = useState(null);
  useEffect(() => { api.get("/settings/early-bird").then((r) => setEb(r.data)).catch(() => {}); }, []);
  const setTaken = async (taken) => {
    if (taken < 0 || (eb && taken > eb.total)) return;
    try {
      const r = await api.put("/settings/early-bird", { taken });
      setEb(r.data);
      toast.success(r.data.active ? `Early Bird: ${r.data.taken}/${r.data.total} ocupate — ${r.data.remaining} rămase` : "Early Bird închis — pe site se afișează doar prețurile standard.");
    } catch { toast.error("Eroare la actualizarea Early Bird."); }
  };

  const updateStatus = async (id, status) => {
    try {
      await api.patch(`/holiday-enquiries/${id}`, { status });
      toast.success(`Status actualizat: ${STATUS_LABELS[status]}`);
      load();
      if (selected?.id === id) setSelected((s) => ({ ...s, status }));
    } catch { toast.error("Eroare la actualizare."); }
  };

  const remove = async (id) => {
    if (!window.confirm("Sigur ștergi cererea?")) return;
    try {
      await api.delete(`/holiday-enquiries/${id}`);
      toast.success("Cerere ștearsă.");
      load();
      if (selected?.id === id) setSelected(null);
    } catch { toast.error("Eroare la ștergere."); }
  };

  return (
    <section className="max-w-[1400px] mx-auto px-6 lg:px-10 pb-20" data-testid="holiday-enquiries-panel">
      {eb && (
        <div className="mb-8 bg-white border border-bone-deep p-5 flex flex-col md:flex-row md:items-center justify-between gap-4" data-testid="early-bird-admin">
          <div className="flex items-start gap-3">
            <Sparkles className="w-5 h-5 text-terracotta mt-0.5" />
            <div>
              <div className="text-[10px] uppercase tracking-wider-3 text-moss">Early Bird 2027 — alocare globală</div>
              <div className="font-display text-2xl text-forest mt-1" data-testid="early-bird-admin-status">
                {eb.taken} / {eb.total} rezervări directe ocupate · {eb.active ? `${eb.remaining} rămase` : "PROMOȚIE ÎNCHISĂ"}
              </div>
              <div className="text-xs text-muted-foreground mt-1">Marchează câte din cele 4 locuri Early Bird au fost confirmate (o rezervare = un loc, indiferent de însoțitori). La 4/4 secțiunea Early Bird se închide automat pe site.</div>
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <button onClick={() => setTaken(eb.taken - 1)} disabled={eb.taken <= 0} data-testid="early-bird-minus" className="w-10 h-10 border border-forest text-forest flex items-center justify-center disabled:opacity-30 hover:bg-forest hover:text-bone transition-colors"><Minus className="w-4 h-4" /></button>
            <span className="font-display text-3xl text-forest w-10 text-center" data-testid="early-bird-taken">{eb.taken}</span>
            <button onClick={() => setTaken(eb.taken + 1)} disabled={eb.taken >= eb.total} data-testid="early-bird-plus" className="w-10 h-10 border border-forest text-forest flex items-center justify-center disabled:opacity-30 hover:bg-forest hover:text-bone transition-colors"><Plus className="w-4 h-4" /></button>
          </div>
        </div>
      )}
      {seats && (
        <div className="mb-8 bg-white border border-bone-deep" data-testid="departures-admin">
          <button type="button" onClick={() => setShowDeps((v) => !v)} data-testid="departures-admin-toggle" className="w-full flex items-center justify-between gap-4 p-5 text-left">
            <div className="flex items-start gap-3">
              <CalendarDays className="w-5 h-5 text-terracotta mt-0.5" />
              <div>
                <div className="text-[10px] uppercase tracking-wider-3 text-moss">Plecări 2027 — locuri rămase</div>
                <div className="font-display text-2xl text-forest mt-1">{DEPARTURES_2027.length} plecări · capacitate 10 locuri fiecare</div>
                <div className="text-xs text-muted-foreground mt-1">Scade locurile pe măsură ce confirmi rezervări. La ≥{GUARANTEE_MIN} înscriși plecarea apare „garantată”; la 0 locuri apare „Fully booked”.</div>
              </div>
            </div>
            <span className="text-xs uppercase tracking-wider-3 text-forest shrink-0">{showDeps ? "Ascunde" : "Gestionează"}</span>
          </button>
          {showDeps && (
            <div className="border-t border-bone-deep divide-y divide-bone-deep">
              {DEPARTURES_2027.map((d, i) => {
                const left = seats[d.id] !== undefined ? seats[d.id] : d.seatsLeft;
                const booked = d.seats - left;
                return (
                  <div key={d.id} className="flex items-center justify-between gap-4 px-5 py-3" data-testid={`dep-admin-${d.id}`}>
                    <div className="flex items-center gap-4">
                      <span className="text-[10px] uppercase tracking-wider-3 text-moss w-6">{String(i + 1).padStart(2, "0")}</span>
                      <span className="font-display text-lg text-forest">{formatDepartureRange(d, "ro")}</span>
                      <span className={`text-[10px] uppercase tracking-wider-2 px-2 py-0.5 ${left === 0 ? "bg-moss text-white" : booked >= GUARANTEE_MIN ? "border border-forest text-forest" : "text-moss"}`}>
                        {left === 0 ? "Complet" : booked >= GUARANTEE_MIN ? "Garantată" : `${booked} înscriși`}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <button onClick={() => setSeatsLeft(d.id, left - 1)} disabled={left <= 0} data-testid={`dep-minus-${d.id}`} className="w-8 h-8 border border-forest text-forest flex items-center justify-center disabled:opacity-30 hover:bg-forest hover:text-bone transition-colors"><Minus className="w-3.5 h-3.5" /></button>
                      <span className="font-display text-xl text-forest w-8 text-center" data-testid={`dep-left-${d.id}`}>{left}</span>
                      <button onClick={() => setSeatsLeft(d.id, left + 1)} disabled={left >= d.seats} data-testid={`dep-plus-${d.id}`} className="w-8 h-8 border border-forest text-forest flex items-center justify-center disabled:opacity-30 hover:bg-forest hover:text-bone transition-colors"><Plus className="w-3.5 h-3.5" /></button>
                      <span className="text-xs text-muted-foreground w-16">locuri</span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      <div className="flex items-center justify-between mb-6 flex-wrap gap-4">
        <div className="flex flex-wrap gap-1 border border-bone-deep" data-testid="holiday-status-filter">
          {["all", "new", "confirmed", "rejected", "completed"].map((s) => (
            <button key={s} onClick={() => setFilter(s)} data-testid={`holiday-filter-${s}`}
              className={`px-5 py-2.5 text-xs uppercase tracking-wider-3 transition-colors ${filter === s ? "bg-forest text-bone" : "bg-white text-forest hover:bg-bone-deep"}`}>
              {s === "all" ? "Toate" : STATUS_LABELS[s]}
            </button>
          ))}
        </div>
        <button onClick={load} data-testid="holiday-refresh-btn" className="flex items-center gap-2 text-xs uppercase tracking-wider-3 text-forest link-underline">
          <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} /> Reîmprospătează
        </button>
      </div>

      <div className="bg-white border border-bone-deep overflow-x-auto">
        <table className="w-full text-sm" data-testid="holiday-enquiries-table">
          <thead className="bg-bone-deep text-forest">
            <tr className="text-left text-[10px] uppercase tracking-wider-3">
              <th className="px-5 py-4">Primită</th>
              <th className="px-5 py-4">Client</th>
              <th className="px-5 py-4">Pachet</th>
              <th className="px-5 py-4">Lună / Plecare</th>
              <th className="px-5 py-4">Rideri / Non-rideri</th>
              <th className="px-5 py-4">Transfer</th>
              <th className="px-5 py-4">Status</th>
              <th className="px-5 py-4">Acțiuni</th>
            </tr>
          </thead>
          <tbody>
            {loading && rows.length === 0 && (
              <tr><td colSpan={8} className="px-5 py-12 text-center text-moss text-xs uppercase tracking-wider-3">Se încarcă…</td></tr>
            )}
            {!loading && rows.length === 0 && (
              <tr><td colSpan={8} className="px-5 py-16 text-center text-moss"><ListChecks className="w-6 h-6 mx-auto mb-3 opacity-50" /><div className="text-xs uppercase tracking-wider-3">Nicio cerere pe acest filtru.</div></td></tr>
            )}
            {rows.map((r) => (
              <tr key={r.id} className={`border-t border-bone-deep hover:bg-bone-deep/40 cursor-pointer ${r.id === focusId ? "bg-terracotta/10 ring-2 ring-inset ring-terracotta" : ""}`} onClick={() => setSelected(r)} data-testid={`holiday-row-${r.id}`}>
                <td className="px-5 py-4 text-xs text-moss whitespace-nowrap">{new Date(r.created_at).toLocaleString("ro-RO")}</td>
                <td className="px-5 py-4">
                  <div className="text-forest flex items-center gap-2">{r.full_name}{r.is_trade && <Briefcase className="w-3.5 h-3.5 text-terracotta" title="Tour operator / club" />}</div>
                  <div className="text-xs text-moss">{r.email} · {r.country}</div>
                </td>
                <td className="px-5 py-4 text-forest">{r.package}</td>
                <td className="px-5 py-4 text-forest"><div>{r.month}</div>{r.departure && <div className="text-xs text-moss">{r.departure}</div>}</td>
                <td className="px-5 py-4">{r.riders} / {r.non_riders}</td>
                <td className="px-5 py-4">{r.transfer}</td>
                <td className="px-5 py-4"><span className={`inline-block px-2.5 py-1 text-[10px] uppercase tracking-wider-2 ${STATUS_STYLES[r.status]}`}>{STATUS_LABELS[r.status]}</span></td>
                <td className="px-5 py-4">
                  <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                    <button title="Confirmă" data-testid={`holiday-confirm-${r.id}`} onClick={() => updateStatus(r.id, "confirmed")} className="p-2 hover:bg-forest hover:text-bone transition-colors"><Check className="w-4 h-4" /></button>
                    <button title="Respinge" data-testid={`holiday-reject-${r.id}`} onClick={() => updateStatus(r.id, "rejected")} className="p-2 hover:bg-destructive hover:text-bone transition-colors"><X className="w-4 h-4" /></button>
                    <button title="Finalizată" data-testid={`holiday-complete-${r.id}`} onClick={() => updateStatus(r.id, "completed")} className="p-2 hover:bg-moss hover:text-bone transition-colors"><ListChecks className="w-4 h-4" /></button>
                    <button title="Șterge" data-testid={`holiday-delete-${r.id}`} onClick={() => remove(r.id)} className="p-2 hover:bg-destructive hover:text-bone transition-colors"><Trash2 className="w-4 h-4" /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {selected && (
        <div className="fixed inset-0 z-50 flex" data-testid="holiday-detail-drawer">
          <div className="flex-1 bg-foreground/40" onClick={() => setSelected(null)} />
          <aside className="w-full sm:w-[480px] bg-bone overflow-y-auto">
            <div className="px-6 py-5 bg-forest text-bone flex items-center justify-between">
              <div>
                <div className="text-[10px] uppercase tracking-wider-3 opacity-70">E-MTB Escape 2027</div>
                <div className="font-display text-2xl mt-1">{selected.package}</div>
              </div>
              <button onClick={() => setSelected(null)} data-testid="holiday-close-drawer" className="text-bone"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-6 space-y-5 text-sm">
              <Row label="Status" value={<span className={`inline-block px-2.5 py-1 text-[10px] uppercase tracking-wider-2 ${STATUS_STYLES[selected.status]}`}>{STATUS_LABELS[selected.status]}</span>} />
              <Row label="Nume" value={selected.full_name} />
              <Row label="Email" value={<a href={`mailto:${selected.email}`} className="link-underline">{selected.email}</a>} />
              <Row label="Țară" value={selected.country} />
              <Row label="Rideri" value={selected.riders} />
              <Row label="Însoțitori non-rider" value={selected.non_riders} />
              <Row label="Lună preferată" value={selected.month} />
              {selected.departure && <Row label="Plecare preferată" value={selected.departure} />}
              <Row label="Experiență" value={selected.experience} />
              <Row label="Transfer aeroport" value={selected.transfer} />
              <Row label="Tour operator / club" value={selected.is_trade ? "DA" : "Nu"} />
              <Row label="Limbă" value={selected.lang} />
              {selected.message && <Row label="Mesaj" value={<span className="whitespace-pre-wrap">{selected.message}</span>} />}
              <Row label="Primită la" value={new Date(selected.created_at).toLocaleString("ro-RO")} />
              <div className="pt-4 grid grid-cols-2 gap-2">
                <button onClick={() => updateStatus(selected.id, "confirmed")} className="btn-lift bg-forest text-bone py-3 text-xs uppercase tracking-wider-3">Confirmă</button>
                <button onClick={() => updateStatus(selected.id, "rejected")} className="btn-lift border border-destructive text-destructive py-3 text-xs uppercase tracking-wider-3">Respinge</button>
                <button onClick={() => updateStatus(selected.id, "completed")} className="btn-lift bg-moss text-white py-3 text-xs uppercase tracking-wider-3">Finalizată</button>
                <button onClick={() => updateStatus(selected.id, "new")} className="btn-lift border border-forest text-forest py-3 text-xs uppercase tracking-wider-3">Marchează nouă</button>
              </div>
            </div>
          </aside>
        </div>
      )}
    </section>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex flex-col gap-1 border-b border-bone-deep pb-3">
      <span className="text-[10px] uppercase tracking-wider-3 text-moss">{label}</span>
      <span className="text-forest">{value}</span>
    </div>
  );
}
